import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FeeService } from '../service/fee.service';
import { SubgroupCreateRequest, SubgroupResponse, SubgroupUpdateRequest } from '../interface/fee.interface';
import { DiscountType } from '../enums/DiscountType';
import { FrequencyType } from '../enums/FrequencyType';
import { TranslateService } from '@ngx-translate/core';
import { extendOnlyEndDateValidator, minSelectableStartDate, OVERPAYMENT_EXTEND_ONLY_ERROR, startOfDay, toApiDateString, toFormDateValue } from '../utils/date.utils';
import { SnackBarUtil } from '../qr-copy-message/snack-bar-util.service';
import { MockupDataModel } from '../qr-copy-message/mockup-data.model';
import { DEFAULT_PAYMENT_FLOW, FEE_PAYMENT_FLOW_OPTIONS, isDailySinglePayFrequency, resolveSubgroupEndDate } from '../utils/fee-frequency.utils';
import { handleDiscountReasonKeyDown, integerValidator, sanitizeDiscountReasonPaste, syncDiscountControlErrors } from '../utils/fee-discount.utils';
import { CLIPBOARD_SHORTCUT_KEYS, NAVIGATION_KEYS } from '../constants/keyboard.constants';

export interface SubgroupFormDialogData {
  mode: 'create' | 'edit';
  groupId?: number;
  subgroup?: SubgroupResponse;
}

@Component({
  selector: 'app-fee-subgroup-form-dialog',
  templateUrl: './fee-subgroup-form-dialog.component.html',
  styleUrls: ['./fee-subgroup-form-dialog.component.scss']
})
export class SubgroupFormDialogComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();
  private discountTypeBeforeOpen: string = '';

  form!: FormGroup;
  finalPrice: number = 0;
  submitting = false;

  readonly minStartDate: Date;
  /** When the subgroup has an overpayment, the end date can only be moved to or after this floor. */
  readonly extendOnlyFloor: Date | null;
  /** Lower bound for the single (DAILY_SINGLE_PAY) date picker, tightened to the floor when extend-only. */
  readonly singlePickerMin: Date;

  readonly discountTypes = Object.values(DiscountType);
  readonly frequencyTypes = Object.values(FrequencyType);
  readonly paymentFlowOptions = FEE_PAYMENT_FLOW_OPTIONS;

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialogRef: MatDialogRef<SubgroupFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) readonly data: SubgroupFormDialogData,
    private readonly feeService: FeeService,
    private readonly translateService: TranslateService,
    private readonly snackBar: SnackBarUtil
  ) {
    this.minStartDate = minSelectableStartDate(this.data.subgroup?.startDate);
    this.extendOnlyFloor = this.resolveExtendOnlyFloor();
    this.singlePickerMin = this.extendOnlyFloor ?? this.minStartDate;
  }

  private resolveExtendOnlyFloor(): Date | null {
    if (this.data.mode !== 'edit' || !this.data.subgroup?.hasOverpayment) {
      return null;
    }
    return startOfDay(this.data.subgroup.endDate ?? this.data.subgroup.startDate);
  }

  get isDailySinglePay(): boolean {
    return isDailySinglePayFrequency(this.form?.get('frequency')?.value);
  }

  ngOnInit(): void {
    this.form = this.buildForm();
    this.updateEndDateValidators();
    this.syncDailySinglePayEndDate();

    this.form.get('frequency')!.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateEndDateValidators();
        this.syncDailySinglePayEndDate();
      });

    this.form.get('startDate')!.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncDailySinglePayEndDate());

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.syncDiscountErrors();
        this.recalculateFinalPrice();
      });
    this.recalculateFinalPrice();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (this.form.get('endDate')!.hasError(OVERPAYMENT_EXTEND_ONLY_ERROR)) {
      this.showOverpaymentExtendOnlyError();
    }
    if (this.form.invalid || this.submitting) return;
    this.submitting = true;

    const operation = this.data.mode === 'create'
      ? this.feeService.createSubgroup(this.data.groupId!, this.mapFormToCreateRequest())
      : this.feeService.updateSubgroup(this.data.subgroup!.id, this.mapFormToUpdateRequest());

    operation.pipe(takeUntil(this.destroy$)).subscribe({
      next: result => this.dialogRef.close(result),
      error: () => { this.submitting = false; }
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  frequencyLabel(freq: string): string {
    const v = (freq || '').toUpperCase();
    const key = `fee.frequency_types.${v}`;
    const translated = this.translateService.instant(key);
    return translated !== key ? translated : freq;
  }

  onDiscountKeyDown(event: KeyboardEvent): void {
    if (this.isKeyboardShortcut(event) || NAVIGATION_KEYS.includes(event.key)) {
      return;
    }
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }
    const input = event.target as HTMLInputElement;
    const caret = input.selectionStart ?? input.value.length;
    if (event.key === '0' && !input.value.slice(0, caret)) {
      event.preventDefault();
    }
  }

  onDiscountPaste(event: ClipboardEvent): void {
    const digitsOnly = this.mergePastedValue(event).replace(/\D/g, '');
    this.form.get('discountValue')!.setValue(digitsOnly === '' ? '' : Number(digitsOnly));
  }

  onReasonKeyDown(event: KeyboardEvent): void {
    handleDiscountReasonKeyDown(event);
  }

  onReasonPaste(event: ClipboardEvent): void {
    this.form.get('reason')!.setValue(sanitizeDiscountReasonPaste(event));
  }

  private isKeyboardShortcut(event: KeyboardEvent): boolean {
    return (event.ctrlKey || event.metaKey) && CLIPBOARD_SHORTCUT_KEYS.includes(event.key.toLowerCase());
  }

  private mergePastedValue(event: ClipboardEvent): string {
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    const pasted = event.clipboardData?.getData('text') ?? '';
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    return `${input.value.slice(0, start)}${pasted}${input.value.slice(end)}`;
  }

  onDiscountSelectOpened(opened: boolean): void {
    if (opened) {
      this.discountTypeBeforeOpen = this.form.get('discountType')!.value || '';
    }
  }

  onDiscountTypeOptionClick(value: string): void {
    if (this.discountTypeBeforeOpen === value) {
      setTimeout(() => this.form.get('discountType')!.setValue(''));
    }
  }

  private buildForm(): FormGroup {
    const sg = this.data.subgroup;
    return this.fb.group(
      {
        name: [sg?.name ?? '', Validators.required],
        mentorName: [sg?.mentorName ?? ''],
        mentorPhone: [sg?.mentorPhone ?? ''],
        mentorEmail: [sg?.mentorEmail ?? ''],
        startDate: [toFormDateValue(sg?.startDate), Validators.required],
        endDate: [toFormDateValue(sg?.endDate)],
        frequency: [{ value: sg?.frequency ?? '', disabled: this.data.mode === 'edit' }, Validators.required],
        paymentFlow: [sg?.paymentFlow ?? DEFAULT_PAYMENT_FLOW, Validators.required],
        price: [sg?.price ?? '', [Validators.required, Validators.min(0.01)]],
        allowCustomAmount: [sg?.allowCustomAmount ?? false],
        reason: [sg?.reason ?? '', Validators.maxLength(30)],
        discountType: [sg?.discountType ?? ''],
        discountValue: [sg?.discountValue || '', [Validators.min(0), integerValidator()]]
      }
    );
  }

  private syncDiscountErrors(): void {
    const price: number = Number(this.form.get('price')!.value) || 0;
    syncDiscountControlErrors(this.form.get('discountType')!, this.form.get('discountValue')!, price);
  }

  private updateEndDateValidators(): void {
    const endCtrl = this.form.get('endDate')!;
    const validators: ValidatorFn[] = this.isDailySinglePay ? [] : [Validators.required];
    if (this.extendOnlyFloor) {
      validators.push(extendOnlyEndDateValidator(this.extendOnlyFloor));
    }
    endCtrl.setValidators(validators);
    endCtrl.updateValueAndValidity({ emitEvent: false });
  }

  private showOverpaymentExtendOnlyError(): void {
    this.snackBar.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: SnackBarUtil.CLASSES.error,
      data: MockupDataModel.error(this.translateService.instant('fee.validation.overpayment_extend_only'))
    });
  }

  private syncDailySinglePayEndDate(): void {
    if (!this.isDailySinglePay) {
      return;
    }
    const start = this.form.get('startDate')!.value;
    if (start) {
      this.form.get('endDate')!.setValue(start, { emitEvent: false });
    }
  }

  private recalculateFinalPrice(): void {
    const price: number = Number(this.form.get('price')!.value) || 0;
    const discountType: string = this.form.get('discountType')!.value;
    const discountValue: number = Number(this.form.get('discountValue')!.value) || 0;

    if (!discountValue || !discountType) {
      this.finalPrice = price;
      return;
    }
    if (discountType === DiscountType.PERCENT) {
      this.finalPrice = price - (price * discountValue / 100);
    } else {
      this.finalPrice = price - discountValue;
    }
  }

  private mapFormToCreateRequest(): SubgroupCreateRequest {
    const v = this.form.getRawValue();
    return {
      name: v.name,
      mentorName: v.mentorName || null,
      mentorPhone: v.mentorPhone || null,
      mentorEmail: v.mentorEmail || null,
      reason: (v.reason || '').trim() || null,
      startDate: toApiDateString(v.startDate)!,
      endDate: resolveSubgroupEndDate(v.frequency, v.startDate, v.endDate),
      frequency: v.frequency,
      paymentFlow: v.paymentFlow || DEFAULT_PAYMENT_FLOW,
      price: Number(v.price),
      allowCustomAmount: !!v.allowCustomAmount,
      discountType: v.discountType || null,
      discountValue: Number(v.discountValue) || 0
    };
  }

  private mapFormToUpdateRequest(): SubgroupUpdateRequest {
    return this.mapFormToCreateRequest();
  }
}
